import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getEstimatedDuration, allDeviceTypes, vehicleCategories } from '../utils';

const initInstallations = (appointment) => {
  if (appointment?.installations && appointment.installations.length > 0) {
    return appointment.installations.map(inst => ({
      ...inst,
      id: inst.id || crypto.randomUUID(),
      extra_jobs: (inst.extra_jobs || []).map(ej => ({
        ...ej,
        id: ej.id || Date.now().toString() + Math.random().toString(36).substr(2),
      })),
    }));
  }
  if (appointment?.job_number || appointment?.vehicle_brand) {
    return [{
      id: crypto.randomUUID(),
      job_number: appointment?.job_number || "",
      device_type: appointment?.device_type || "",
      estimated_duration: appointment?.estimated_duration || null,
      vehicle_brand: appointment?.vehicle_brand || "",
      vehicle_model: appointment?.vehicle_model || "",
      license_plate: appointment?.license_plate || "",
      chassis_number: appointment?.chassis_number || "",
      extra_jobs: [],
    }];
  }
  return [{
    id: crypto.randomUUID(),
    job_number: "", device_type: "", estimated_duration: null,
    vehicle_brand: "", vehicle_model: "", license_plate: "", chassis_number: "",
    extra_jobs: [],
  }];
};

export const useAppointmentForm = (appointment, onSave, t) => {
  const [formData, setFormData] = useState({
    team_id: appointment?.team_id || "",
    assigned_installer_id: appointment?.assigned_installer_id || "",
    client_name: appointment?.client_name || "",
    client_tax_id: appointment?.client_tax_id || "",
    member_number: appointment?.member_number || "",
    intervention_type: appointment?.intervention_type || "installation",
    absence_type: appointment?.absence_type || null,
    absence_full_day: appointment?.absence_full_day !== undefined ? appointment.absence_full_day : null,
    // "Altro": estrai il motivo dal client_name salvato (pattern "Altro: <motivo> - <installer>")
    other_reason: appointment?.intervention_type === 'other'
      ? ((appointment.client_name || '').replace(/^Altro:\s*/, '').replace(/\s*-\s*[^-]+$/, '').trim())
      : null,
    site_name: appointment?.site_name || "",
    site_address: appointment?.site_address || "",
    site_city: appointment?.site_city || "",
    site_province: appointment?.site_province || "",
    contact_name: appointment?.contact_name || "",
    contact_phone: appointment?.contact_phone || "",
    address: appointment?.address || "",
    city: appointment?.city || "",
    province: appointment?.province || "",
    installation_type: appointment?.installation_type || "standard",
    vehicle_category: appointment?.vehicle_category || "auto",
    scheduled_date: appointment?.scheduled_date ? appointment.scheduled_date.split("T")[0] : new Date().toISOString().split("T")[0],
    scheduled_time: appointment?.scheduled_time || "",
    scheduled_end_time: appointment?.scheduled_end_time || "",
    confirmation_status: appointment?.status === "provisional" ? "provisional" : appointment?.confirmation_status === "provisional" ? "provisional" : (appointment ? (appointment.status || "confirmed") : "confirmed"),
    recurrence_type: "none",
    recurrence_end_date: "",
    notes: appointment?.notes || "",
    manual_address: appointment?.manual_address || false,
    absence_end_date: "",
    end_time_manual: !!appointment?.scheduled_end_time,
  });

  const [installations, setInstallations] = useState(() => initInstallations(appointment));
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [pendingSeriesSave, setPendingSeriesSave] = useState(null);

  const confirmSeriesScope = async (scope) => {
    if (!pendingSeriesSave) return;
    setLoading(true);
    try {
      const { payload, appointmentId } = pendingSeriesSave;
      await axios.put(`${API}/appointments/${appointmentId}?series_scope=${scope}`, payload);
      const msg = scope === "all" ? "Modifiche applicate a tutta la serie ricorrente"
        : scope === "future" ? "Modifiche applicate a questo e ai successivi appuntamenti"
        : (t('appointment.messages.updated') || "Appuntamento aggiornato");
      toast.success(msg);
      setPendingSeriesSave(null);
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('appointment.messages.saveError') || 'Errore salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const cancelSeriesScope = () => setPendingSeriesSave(null);

  const calculateTotalDuration = () => {
    // Bonus veicolo (Moto +60, Supercar/Scooter +30, Auto 0) va applicato UNA volta per
    // installazione, non moltiplicato per ogni extra_job. In precedenza calcolo sbagliato
    // dava 10h invece di 8h per Sergiu con 4 mezzi + moto + extra job (bonus contato x2).
    const vehicle = vehicleCategories.find(v => v.value === formData.vehicle_category);
    const vehicleBonus = vehicle ? vehicle.extraMinutes : 0;
    return installations.reduce((total, inst) => {
      const mainBase = inst.device_type
        ? (allDeviceTypes.find(d => d.value === inst.device_type)?.duration || 0)
        : 0;
      const extraBase = (inst.extra_jobs || []).reduce((acc, ej) => {
        const d = ej.device_type
          ? (allDeviceTypes.find(x => x.value === ej.device_type)?.duration || 0)
          : 0;
        return acc + d;
      }, 0);
      const hasAny = !!inst.device_type || (inst.extra_jobs || []).some(ej => !!ej.device_type);
      return total + mainBase + extraBase + (hasAny ? vehicleBonus : 0);
    }, 0);
  };

  const addInstallation = () => {
    setInstallations(prev => [...prev, {
      id: crypto.randomUUID(),
      job_number: "", device_type: "", estimated_duration: null,
      vehicle_brand: "", vehicle_model: "", license_plate: "", chassis_number: "",
      extra_jobs: [],
    }]);
  };

  const removeInstallation = (id) => {
    if (installations.length > 1) {
      setInstallations(prev => prev.filter(inst => inst.id !== id));
    }
  };

  const updateInstallation = (id, field, value) => {
    setInstallations(prev => prev.map(inst => {
      if (inst.id === id) {
        const updated = { ...inst, [field]: value };
        if (field === 'device_type') {
          updated.estimated_duration = getEstimatedDuration(value, formData.vehicle_category);
        }
        return updated;
      }
      return inst;
    }));
  };

  // Fetch installers when team changes
  useEffect(() => {
    if (formData.team_id) {
      fetchInstallers(formData.team_id);
    } else {
      setInstallers([]);
      setFormData(prev => ({ ...prev, assigned_installer_id: "" }));
    }
  }, [formData.team_id]);

  const fetchInstallers = async (teamId) => {
    try {
      const res = await axios.get(`${API}/teams/${teamId}/installers`);
      setInstallers(res.data);
    } catch (e) {
      console.error("Error fetching installers:", e);
      setInstallers([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isProvisional = formData.confirmation_status === "provisional";
    const isAbsence = formData.intervention_type === "absence";
    const isOther = formData.intervention_type === "other";

    // "Altro": richiede solo installatore, data, ora inizio, ora fine, motivo
    if (isOther) {
      if (!formData.assigned_installer_id) {
        toast.error('Seleziona il tecnico assegnato');
        return;
      }
      if (!formData.scheduled_date) {
        toast.error('Data obbligatoria');
        return;
      }
      if (!formData.scheduled_time || formData.scheduled_time === '00:00') {
        toast.error('Ora inizio obbligatoria');
        return;
      }
      if (!formData.scheduled_end_time || formData.scheduled_end_time === '00:00') {
        toast.error('Ora fine obbligatoria');
        return;
      }
      if (!formData.other_reason || formData.other_reason.trim() === '') {
        toast.error('Il motivo è obbligatorio');
        return;
      }
    } else if (isAbsence) {
      if (!formData.absence_type) {
        toast.error('Seleziona il motivo dell\'assenza');
        return;
      }
      if (!formData.assigned_installer_id) {
        toast.error('Seleziona il tecnico in assenza');
        return;
      }
      if (!formData.scheduled_date) {
        toast.error('Data assenza obbligatoria');
        return;
      }
      if (formData.absence_full_day === false) {
        if (!formData.scheduled_time || !formData.scheduled_end_time) {
          toast.error('Specifica orario inizio e fine assenza');
          return;
        }
      }
    } else if (isProvisional) {
      // For provisional appointments, only require dates
      if (!formData.scheduled_date) {
        toast.error('Data inizio obbligatoria');
        return;
      }
      // Provisional appointments still need a start time to be visible on the calendar.
      // Empty / "00:00" times make the block render outside the visible grid (bug #1).
      if (!formData.scheduled_time || formData.scheduled_time === '00:00') {
        toast.error('Ora inizio obbligatoria: senza orario l\'appuntamento non compare sul calendario');
        return;
      }
    } else {
      // Full validation for confirmed appointments
      if (!formData.scheduled_time || formData.scheduled_time === '00:00') {
        toast.error('Ora inizio obbligatoria: senza orario l\'appuntamento non compare sul calendario');
        return;
      }
      // Per interventi CA (Controllo Anomalia): job_number / chassis_number / device_type /
      // device_delivery sono opzionali. Il tecnico spesso non ha i dati completi fino a
      // sopralluogo. Richiesta Veronica 2026-07-02.
      const isCA = formData.intervention_type === 'ca';
      const firstInstallation = installations[0];
      if (!isCA) {
        if (!firstInstallation?.job_number || firstInstallation.job_number.trim() === '') {
          toast.error(t('appointment.jobNumberRequired') || 'Il numero Job e obbligatorio per la prima installazione');
          return;
        }
      }

      for (let i = 0; i < installations.length; i++) {
        const inst = installations[i];
        if (isCA) {
          // Per CA: richiedi almeno Marca e Modello (info identificativa veicolo)
          if (!inst.vehicle_brand || !inst.vehicle_model) {
            toast.error(`Installazione ${i + 1}: compilare Marca e Modello`);
            return;
          }
          continue;
        }
        if (!inst.job_number || !inst.vehicle_brand || !inst.vehicle_model) {
          toast.error(`Installazione ${i + 1}: compilare Job Number, Marca e Modello`);
          return;
        }
        if (!inst.chassis_number || inst.chassis_number.trim() === '') {
          toast.error(`Installazione ${i + 1}: il campo Telaio e obbligatorio`);
          return;
        }
        if (!inst.device_type || inst.device_type.trim() === '') {
          toast.error(`Installazione ${i + 1}: il Tipo Dispositivo e obbligatorio`);
          return;
        }
        if (!inst.device_delivery || inst.device_delivery.trim() === '') {
          // Per le disinstallazioni la consegna dispositivo non e' richiesta (Veronica 2026-07-07)
          if (formData.intervention_type !== 'uninstall' && formData.intervention_type !== 'deinstallation') {
            toast.error(`Installazione ${i + 1}: la Consegna Dispositivo e obbligatoria`);
            return;
          }
        }
      }
    }

    setLoading(true);
    try {
      const totalDuration = calculateTotalDuration();
      const firstInst = installations[0];

      // Absence appointments have a simplified payload (no installations, no client/site required)
      let payload;
      if (isOther) {
        const installerUser = installers.find(i => i.id === formData.assigned_installer_id);
        const tecName = installerUser ? `${installerUser.first_name} ${installerUser.last_name}` : '';
        const motivo = (formData.other_reason || '').trim();
        payload = {
          ...formData,
          scheduled_date: new Date(formData.scheduled_date).toISOString(),
          scheduled_time: formData.scheduled_time,
          scheduled_end_time: formData.scheduled_end_time,
          assigned_installer_id: formData.assigned_installer_id,
          status: formData.confirmation_status || 'confirmed',
          // Il "motivo" diventa il titolo visualizzato nel calendario (come per le assenze)
          client_name: `Altro: ${motivo}${tecName ? ' - ' + tecName : ''}`,
          installations: [],
          total_duration: null,
        };
      } else if (isAbsence) {
        const absenceLabels = { vacation: 'Ferie', sick_leave: 'Malattia', holiday: 'Festivita', other: 'Altro' };
        const absenceLabel = absenceLabels[formData.absence_type] || 'Assenza';
        const installerName = installers.find(i => i.id === formData.assigned_installer_id);
        const tecName = installerName ? `${installerName.first_name} ${installerName.last_name}` : '';
        payload = {
          ...formData,
          scheduled_date: new Date(formData.scheduled_date).toISOString(),
          scheduled_time: formData.absence_full_day !== false ? '08:00' : (formData.scheduled_time || '08:00'),
          scheduled_end_time: formData.absence_full_day !== false ? '18:00' : (formData.scheduled_end_time || '18:00'),
          assigned_installer_id: formData.assigned_installer_id,
          status: 'confirmed',
          client_name: `${absenceLabel}${tecName ? ' - ' + tecName : ''}`,
          installations: [],
          total_duration: formData.absence_full_day !== false ? 600 : null,
        };
      } else {
        payload = {
          ...formData,
          scheduled_date: new Date(formData.scheduled_date).toISOString(),
          assigned_installer_id: formData.assigned_installer_id || null,
          status: formData.confirmation_status || "confirmed",
          job_number: firstInst.job_number,
          device_type: firstInst.device_type || null,
          vehicle_brand: firstInst.vehicle_brand,
          vehicle_model: firstInst.vehicle_model,
          license_plate: firstInst.license_plate || null,
          chassis_number: firstInst.chassis_number || null,
          estimated_duration: firstInst.device_type ? getEstimatedDuration(firstInst.device_type, formData.vehicle_category) : null,
          installations: installations.map(inst => ({
            ...inst,
            estimated_duration: inst.device_type ? getEstimatedDuration(inst.device_type, formData.vehicle_category) : null,
            extra_jobs: (inst.extra_jobs || []).map(ej => ({
              job_number: ej.job_number,
              device_type: ej.device_type || null,
            })),
          })),
          total_duration: totalDuration
        };
      }

      // Remove frontend-only fields
      delete payload.confirmation_status;
      delete payload.recurrence_type;
      delete payload.recurrence_end_date;
      delete payload.absence_end_date;
      delete payload.end_time_manual;
      delete payload.other_reason;

      if (appointment) {
        // If editing a recurring appointment, defer the save to allow user to pick the scope via custom modal
        if (appointment.recurrence_group_id) {
          setPendingSeriesSave({ payload, appointmentId: appointment.id });
          setLoading(false);
          return;
        }
        await axios.put(`${API}/appointments/${appointment.id}`, payload);
        toast.success(t('appointment.messages.updated'));
      } else {
        // Assenza multi-giorno (range DA/A): una POST per ogni giorno del periodo
        if (isAbsence && formData.absence_end_date && formData.absence_end_date > formData.scheduled_date) {
          const days = [];
          const d = new Date(formData.scheduled_date + 'T12:00:00');
          const end = new Date(formData.absence_end_date + 'T12:00:00');
          while (d <= end) {
            days.push(d.toISOString());
            d.setDate(d.getDate() + 1);
          }
          if (days.length > 62) {
            toast.error('Il periodo di assenza non può superare i 62 giorni');
            setLoading(false);
            return;
          }
          for (const dayIso of days) {
            await axios.post(`${API}/appointments`, { ...payload, scheduled_date: dayIso });
          }
          toast.success(`${days.length} assenze create (${new Date(days[0]).toLocaleDateString('it-IT')} - ${new Date(days[days.length - 1]).toLocaleDateString('it-IT')})`);
        } else if (formData.recurrence_type !== "none" && formData.recurrence_end_date) {
          const res = await axios.post(`${API}/appointments/recurring`, {
            ...payload,
            recurrence_type: formData.recurrence_type,
            recurrence_end_date: new Date(formData.recurrence_end_date).toISOString(),
          });
          const count = res.data?.created_count || 1;
          toast.success(`${count} appuntamenti ricorrenti creati!`);
        } else {
          const res = await axios.post(`${API}/appointments`, payload);
          toast.success(t('appointment.messages.created'));
          // Allega i documenti selezionati in fase di creazione (richiesta Veronica 2026-07-09)
          const newId = res.data?.id;
          if (newId && pendingFiles.length > 0) {
            let uploaded = 0;
            for (const f of pendingFiles) {
              const fd = new FormData();
              fd.append('file', f);
              try {
                await axios.post(`${API}/appointments/${newId}/documents`, fd, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                });
                uploaded += 1;
              } catch (err) {
                toast.error(`Documento "${f.name}" non caricato: ${err.response?.data?.detail || 'errore'}`);
              }
            }
            if (uploaded > 0) toast.success(`${uploaded} document${uploaded === 1 ? 'o allegato' : 'i allegati'}`);
          }
        }
      }
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('appointment.messages.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return {
    formData, setFormData,
    installations, addInstallation, removeInstallation, updateInstallation,
    loading, installers, calculateTotalDuration, handleSubmit,
    pendingSeriesSave, confirmSeriesScope, cancelSeriesScope,
    pendingFiles, setPendingFiles,
  };
};
